import { Photo } from './Photo';
import { distanceLatLng } from './utils';

type EdgeBehavior = 'loop' | 'exit' | 'nothing';
type PhotoWithGeo = Photo & { geo: NonNullable<Photo['geo']> };
type GroupedPhotos = Record<string, Photo[]>;

export class Photos {
  constructor(private photos: Photo[]) { }

  get count(): number {
    return this.photos.length;
  }

  getPhotoById(id: string): Photo | null {
    return this.photos.find(p => p.id === id) ?? null;
  }

  // Navigation methods
  private relativeIndex(currentIndex: number, offset: number, behavior: EdgeBehavior = 'loop'): number | null {
    const length = this.photos.length;
    if (length === 0) return null;

    let newIndex = currentIndex + offset;

    switch (behavior) {
      case 'loop':
        if (newIndex >= length) newIndex = 0;
        if (newIndex < 0) newIndex = length - 1;
        break;
      case 'exit':
        if (newIndex >= length || newIndex < 0) return null;
        break;
      case 'nothing':
        if (newIndex >= length || newIndex < 0) newIndex = currentIndex;
        break;
    }

    return newIndex;
  }

  private getPhotoIndex(photo: Photo | null): number {
    if (!photo) return -1;
    return this.photos.findIndex(p => p.id === photo.id);
  }

  prevPhoto(photo: Photo | null, behavior: EdgeBehavior = 'loop'): Photo | null {
    const currentIndex = this.getPhotoIndex(photo);
    if (currentIndex === -1) return null;
    const newIndex = this.relativeIndex(currentIndex, -1, behavior);
    return newIndex === null ? null : this.photos[newIndex];
  }

  nextPhoto(photo: Photo | null, behavior: EdgeBehavior = 'loop'): Photo | null {
    const currentIndex = this.getPhotoIndex(photo);
    if (currentIndex === -1) return null;
    const newIndex = this.relativeIndex(currentIndex, 1, behavior);
    return newIndex === null ? null : this.photos[newIndex];
  }

  url(photo: Photo | null): string {
    return photo?.url() ?? '';
  }

  prevUrl(photo: Photo | null, behavior: EdgeBehavior = 'loop'): string {
    return this.url(this.prevPhoto(photo, behavior));
  }

  nextUrl(photo: Photo | null, behavior: EdgeBehavior = 'loop'): string {
    return this.url(this.nextPhoto(photo, behavior));
  }

  redirectTo(photo: Photo | null): void {
    window.location.href = photo?.imageUrl ?? '';
  }

  get all(): Photo[] {
    return this.photos;
  }

  // Geo-related methods
  get withGeo(): PhotoWithGeo[] {
    return this.photos.filter((p): p is PhotoWithGeo => p.hasGeo());
  }

  distanceBetween(photo1: Photo, photo2: Photo): number | null {
    if (!photo1.hasGeo() || !photo2.hasGeo()) {
      return null;
    }
    return distanceLatLng(photo1.geo!, photo2.geo!);
  }

  nearestPhotos(photo: PhotoWithGeo, count: number = 3): PhotoWithGeo[] {
    if (!photo.hasGeo()) {
      return [];
    }

    const photosWithGeo = this.withGeo.filter(p => p.id !== photo.id);
    return photosWithGeo
      .sort((a, b) => {
        const distA = this.distanceBetween(photo, a) || Infinity;
        const distB = this.distanceBetween(photo, b) || Infinity;
        return distA - distB;
      })
      .slice(0, count);
  }

  // Date-related methods
  get groupedByDate(): GroupedPhotos {
    return this.photos
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .reduce((acc, photo) => {
        const key = photo.monthYear();
        acc[key] = [...(acc[key] || []), photo];
        return acc;
      }, {} as GroupedPhotos);
  }

  // Location filtering methods
  get states(): string[] {
    return [...new Set(
      this.photos
        .map((photo) => photo.geo?.state || '')
        .filter((state) => state.length > 0))].sort();
  }

  names(): string[] {
    return [...new Set(
      this.photos
        .filter((photo) => photo.hasGeo())
        .map((photo) => photo.geo?.name || '')
        .filter((name) => name.length > 0))
    ].sort();
  }

  provinces(state?: string): string[] {
    return [...new Set(
      this.photos
        .filter((photo) => photo.geo?.state === state || !state)
        .map((photo) => photo.geo?.province || '')
        .filter((province) => province.length > 0))
    ].sort();
  }

  regions(state?: string, province?: string): string[] {
    return [...new Set(
      this.photos
        .filter((photo) => photo.geo?.state === state || !state)
        .filter((photo) => photo.geo?.province === province || !province)
        .map((photo) => photo.geo?.region || '')
        .filter((region) => region.length > 0))
    ].sort();
  }

  types(): string[] {
    return [...new Set(
      this.photos
        .map((photo) => photo.type || '')
        .filter((type) => type.length > 0))
    ].sort();
  }

  filterBy(state?: string | null, province?: string | null, region?: string | null, type?: string | null): Photo[] {
    return this.photos.filter(photo => {
      if (!state && !province && !region && !type) {
        return true;
      }

      let geoMatch = true;
      if (state || province || region) {
        geoMatch = false;
        if (photo.hasGeo()) {
          const { state: photoState, province: photoProvince, region: photoRegion } = photo.geo!;
          geoMatch = (!state || photoState === state) &&
            (!province || photoProvince === province) &&
            (!region || photoRegion === region);
        }
      }

      const typeMatch = !type || photo.type === type;

      return geoMatch && typeMatch;
    });
  }
}
